import { Box, Typography } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import FormDatePicker from '../form/FormDatePicker';
import FormTextField from '../form/FormTextField';
import moment from 'moment';

// Returns the invoicing forecast lines linked to this invoice (via invoice_detail -> invoicing_forecast_details)
const getLinkedForecasts = (invoice, invoicingForecasts) => {
    const detailIds = (invoice?.invoice_details || []).map(d => d.id);
    if(!detailIds.length || !invoicingForecasts?.length) return [];
    return invoicingForecasts.filter(forecast =>
        (forecast.invoicing_forecast_details || []).some(fd => detailIds.includes(fd.invoice_detail_id))
    );
}

const DialogEditInvoice = ({ open , setOpen, editAction, editObject,subfolder,invoiceEmittedId,handleEditDoc,invoicingForecasts,editInvoicingForecast}) => {
    const { t } = useTranslation();
    const [value,setValue] = useState(null);
    const [mismatchOpen,setMismatchOpen] = useState(false);
    const [mismatchedForecasts,setMismatchedForecasts] = useState([]);
    const [pendingSubmit,setPendingSubmit] = useState(null);


    const form = useForm({
      defaultValues:{
          memo:'',
          comment:'',
          number:null,
          issue_date: null,
          term_date: null,
          payment_date: null,
      }
  })


    useEffect(()=>{
      if(editObject){
        setValue(editObject);
            form.reset({
              memo:editObject ? editObject.memo : '',
              comment:editObject ? editObject.comment : '',
              number:editObject ? editObject.number : '',
              issue_date:editObject ? editObject.issue_date : '',
              payment_date:editObject ? editObject.payment_date : '',
              term_date:editObject ?  moment(editObject.issue_date).add(30,'days') : '',
              payment_date:editObject ? editObject.payment_date : '',

            })
        }
        // eslint-disable-next-line
    },[editObject])


    const handleClose = () => {
        setOpen(false);
    };

    const submitInvoice = (val) => {
      editAction({
        id:value.id,
        data:{
          memo:val.memo,
          comment:val.comment,
          number:val.number,
          issue_date:val.issue_date,
          term_date:val.term_date,
          payment_date:val.payment_date
        }
      })
    }

    const handleValidate = (val) => {
      if(!val.number || !val.number.trim()){
        form.setError('number', { type: 'required', message: 'Invoice number is required' });
        return;
      }

      // consistency check: issue_date must match the forecast_date of the linked forecast line(s)
      const linkedForecasts = getLinkedForecasts(value, invoicingForecasts);
      const mismatches = linkedForecasts.filter(forecast =>
        moment(forecast.forecast_date).format('YYYY-MM-DD') !== moment(val.issue_date).format('YYYY-MM-DD')
      );

      if(mismatches.length > 0){
        setMismatchedForecasts(mismatches);
        setPendingSubmit(val);
        setMismatchOpen(true);
        return;
      }

      submitInvoice(val);
    }

    const handleAlignForecastDates = () => {
      Promise.all(mismatchedForecasts.map(forecast => editInvoicingForecast({
        id: forecast.id,
        invoicingForecast: { forecast_date: pendingSubmit.issue_date }
      }))).then(() => {
        submitInvoice(pendingSubmit);
        setMismatchOpen(false);
        setPendingSubmit(null);
      });
    }

    const handleKeepEditing = () => {
      setMismatchOpen(false);
      setPendingSubmit(null);
    }

  return (
    <div>
      <Dialog open={open} onClose={handleClose} aria-labelledby="form-dialog-title">
        <DialogTitle id="form-dialog-title">{t('folder.subfolder.invoice.edit.titleEdit', 'Edit invoice')}</DialogTitle>
        <DialogContent>
          <FormProvider {...form}>
              {value ? <Box display="flex" flexDirection="column">
                <FormTextField label="Description" name="memo"/>
                <FormTextField label="number" name="number"/>
                <FormTextField label="commentaire interne" name="comment"/>
                <FormDatePicker label="Issue date" name="issue_date"/>
                <FormDatePicker label="Term date" name="term_date"/>
                <FormDatePicker label="Payment date" name="payment_date"/>
                {invoiceEmittedId && (<Button onClick={handleEditDoc} style={{marginTop:"20px"}} variant="outlined">Edit document</Button>)}
              </Box>:null}
          </FormProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            {t('button.cancel',"Cancel")}
          </Button>
          <Button onClick={form.handleSubmit(handleValidate)} color="primary" variant="contained">
          {t('button.save',"Save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={mismatchOpen} onClose={handleKeepEditing} maxWidth="sm" fullWidth>
        <DialogTitle>{t('folder.subfolder.invoice.edit.dateMismatchTitle', 'Invoice date does not match the forecast')}</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            {pendingSubmit && `The invoice date you entered (${moment(pendingSubmit.issue_date).format('DD/MM/YYYY')}) does not match the invoicing forecast date${mismatchedForecasts.length > 1 ? 's' : ''} below:`}
          </Typography>
          <Box marginTop={2} marginBottom={2}>
            {mismatchedForecasts.map(forecast => (
              <Typography key={forecast.id}>
                - {forecast.memo || 'Forecast line'} : {moment(forecast.forecast_date).format('DD/MM/YYYY')}
              </Typography>
            ))}
          </Box>
          <Typography>
            {t('folder.subfolder.invoice.edit.dateMismatchQuestion', 'Do you want to update the forecast date(s) to match the invoice date, or go back and correct the invoice date instead?')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleKeepEditing} color="primary">
            {t('folder.subfolder.invoice.edit.dateMismatchKeepEditing', 'Go back and fix invoice date')}
          </Button>
          <Button onClick={handleAlignForecastDates} color="primary" variant="contained">
            {t('folder.subfolder.invoice.edit.dateMismatchAlign', 'Update forecast date(s) to match')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default DialogEditInvoice
